/**
 * The respondent's own screens, live, inside a phone (Z-124).
 *
 * Not a drawing of them: `Landing`, `Consent`, `StimulusScreen`, `Questions`
 * and `ThankYou` are the components a respondent gets at /r/<token>, fed a
 * study built from the draft. They render into an iframe sized like a phone
 * (390 × 844), so their breakpoints, `100dvh` and `position: fixed` resolve
 * against the phone and not against the wizard — the preview lays out exactly
 * the way a phone does. The frame is then scaled down to fit the column.
 *
 * The screens stay clickable: "Rozpocznij badanie" opens the consent, consent
 * plays the material, the material ends in the questions, in the order the
 * study will use. The camera check and gaze calibration are skipped — they
 * would ask the researcher for their own camera.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Box, CssBaseline, GlobalStyles, Typography } from '@mui/material'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import { useTranslation } from 'react-i18next'
import { createAffexyTheme } from '@/theme/affexyTheme'
import { editableToApiQuestion } from '@/components/questions/apiAdapter'
import type { RespondentQuestion, RespondentStimulus, RespondentStudy } from '@/services/respondentService'
import { Consent, Landing, Questions, StimulusScreen, ThankYou } from '@/pages/RespondentJourney'
import { RESPONDENT_KEYFRAMES } from '@/components/respondent/keyframes'
import { askableQuestions } from './askable'
import type { StimulusDraft, StudyDraft } from './types'

export type PreviewScreen = 'landing' | 'consent' | 'stimulus' | 'questions' | 'thanks'

const PHONE_W = 390
const PHONE_H = 844
const BEZEL = 9

/** Object URLs for picked files that are not uploaded yet, revoked on unmount. */
function useFileUrls() {
  const urls = useRef(new Map<File, string>())
  useEffect(() => {
    const map = urls.current
    return () => {
      map.forEach((u) => URL.revokeObjectURL(u))
      map.clear()
    }
  }, [])
  return useCallback((file: File) => {
    let u = urls.current.get(file)
    if (!u) {
      u = URL.createObjectURL(file)
      urls.current.set(file, u)
    }
    return u
  }, [])
}

function toStimulus(s: StimulusDraft, i: number, fileUrl: (f: File) => string): RespondentStimulus | null {
  // Audio has no respondent screen yet; the launch does not persist it either.
  if (s.type === 'audio') return null
  const url = s.file ? fileUrl(s.file) : (s.url ?? '')
  return {
    id: i + 1,
    type: s.type,
    title: s.name,
    url,
    storage_url: url,
    payload: s.payload ?? '',
    order: i,
    exposure_ms: s.exposureMs ?? null,
    config: s.type === 'figma' ? { goal_node_id: s.goalNodeId ?? '' } : undefined,
  }
}

/** The study as /api/public/respondent/ would hand it to the respondent. */
function useDraftStudy(draft: StudyDraft): RespondentStudy {
  const fileUrl = useFileUrls()
  return useMemo(() => {
    const questions: RespondentQuestion[] = askableQuestions(draft.questions, draft.demographics).map((q, i) => {
      const api = editableToApiQuestion(q)
      return { id: i + 1, unique_number: i + 1, text: api.text, question_type: api.question_type, section_name: api.section_name, choices: api.choices }
    })
    return {
      share_token: 'preview',
      name: draft.name.trim(),
      client: draft.client.trim(),
      // The team note never reaches the respondent (see WizardStepBasics), so
      // the invitation falls back to its generated sentence, as it will live.
      description: '',
      require_access_code: draft.requireAccessCode && !draft.isPublic,
      eye_tracking_enabled: draft.eyeTracking,
      calibration_variant: draft.calibrationVariant,
      requires_desktop: draft.stimuli.some((s) => s.type === 'figma'),
      instructions: '',
      question_timing: draft.questionTiming,
      stimuli: draft.stimuli.map((s, i) => toStimulus(s, i, fileUrl)).filter((s): s is RespondentStimulus => !!s),
      questions,
    }
  }, [draft, fileUrl])
}

/** Renders children into a same-origin iframe with its own viewport. */
function Viewport({ width, height, children }: { width: number; height: number; children: ReactNode }) {
  const ref = useRef<HTMLIFrameElement>(null)
  const [doc, setDoc] = useState<Document | null>(null)

  const onLoad = () => {
    const d = ref.current?.contentDocument
    if (!d) return
    // Fonts and global CSS of the app; emotion styles come through the cache below.
    document.head.querySelectorAll('link[rel="stylesheet"], style:not([data-emotion])').forEach((n) => d.head.appendChild(n.cloneNode(true)))
    // A link in a preview must not leave it (the results button, a stray href).
    d.addEventListener('click', (e) => {
      if ((e.target as Element | null)?.closest?.('a')) e.preventDefault()
    })
    setDoc(d)
  }

  const cache = useMemo(() => (doc ? createCache({ key: 'fwprev', container: doc.head, prepend: true }) : null), [doc])
  const theme = useMemo(() => createAffexyTheme(), [])

  return (
    <Box
      component="iframe"
      ref={ref}
      title="preview"
      onLoad={onLoad}
      srcDoc={`<!doctype html><html lang="pl"><head><meta charset="utf-8"><meta name="viewport" content="width=${width}"></head><body></body></html>`}
      sx={{ width, height, border: 0, display: 'block', bgcolor: '#fff' }}
    >
      {doc &&
        cache &&
        createPortal(
          <CacheProvider value={cache}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <GlobalStyles styles={{ ...RESPONDENT_KEYFRAMES, body: { overflowX: 'hidden' }, '::-webkit-scrollbar': { width: 0 } }} />
              {children}
            </ThemeProvider>
          </CacheProvider>,
          doc.body,
        )}
    </Box>
  )
}

/** Phone height that fits the window under the wizard's bars. */
function usePhoneHeight(): number {
  const read = () => Math.max(460, Math.min(720, (typeof window === 'undefined' ? 900 : window.innerHeight) - 330))
  const [h, setH] = useState(read)
  useLayoutEffect(() => {
    const on = () => setH(read())
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  return h
}

export default function LivePreview({ draft, initialScreen }: { draft: StudyDraft; initialScreen: PreviewScreen }) {
  const c = useTheme().affexy.colors
  const { t } = useTranslation('dashboard')
  const study = useDraftStudy(draft)
  const [screen, setScreen] = useState<PreviewScreen>(initialScreen)
  // Bumped to restart a screen that is already open (the material, mainly).
  const [run, setRun] = useState(0)

  const hasQuestions = study.questions.length > 0
  const go = useCallback((s: PreviewScreen) => {
    setScreen(s)
    setRun((r) => r + 1)
  }, [])

  // A new wizard step shows its own screen.
  useEffect(() => go(initialScreen === 'questions' && !hasQuestions ? 'landing' : initialScreen), [initialScreen, hasQuestions]) // eslint-disable-line react-hooks/exhaustive-deps

  const start = study.question_timing === 'start'
  const afterConsent: PreviewScreen = start && hasQuestions ? 'questions' : 'stimulus'
  const afterStimulus: PreviewScreen = !start && hasQuestions ? 'questions' : 'thanks'
  const afterQuestions: PreviewScreen = start ? 'stimulus' : 'thanks'

  // Same numbering as the live journey (the camera check counts, calibration does not).
  const order: PreviewScreen[] = start ? ['landing', 'consent', 'questions', 'stimulus', 'thanks'] : ['landing', 'consent', 'stimulus', 'questions', 'thanks']
  const stepIdx = (s: PreviewScreen) => {
    const i = order.indexOf(s)
    return i >= order.indexOf('stimulus') ? i + 1 : i
  }

  const content =
    screen === 'landing' ? (
      <Landing study={study} onStart={() => go('consent')} stepIdx={stepIdx('landing')} />
    ) : screen === 'consent' ? (
      <Consent
        onAgree={async () => go(afterConsent)}
        requireCode={study.require_access_code}
        eyeTracking={study.eye_tracking_enabled}
        client={study.client}
        stepIdx={stepIdx('consent')}
      />
    ) : screen === 'stimulus' ? (
      <StimulusScreen
        study={study}
        recordStartRef={{ current: 0 }}
        onMarker={() => {}}
        prototypeEmbeds={{}}
        onPrototypeSession={() => {}}
        onDone={() => go(afterStimulus)}
      />
    ) : screen === 'questions' ? (
      <PreviewQuestions study={study} stepIdx={stepIdx('questions')} onFinish={() => go(afterQuestions)} />
    ) : (
      <ThankYou study={study} resultsHref="#" />
    )

  const height = usePhoneHeight()
  const scale = (height - 2 * BEZEL) / PHONE_H
  const width = Math.round(PHONE_W * scale + 2 * BEZEL)

  const tabs: { id: PreviewScreen; label: string; disabled?: boolean }[] = [
    { id: 'landing', label: t('wizard.desk.preview.landing') },
    { id: 'consent', label: t('wizard.desk.preview.consent') },
    { id: 'stimulus', label: t('wizard.desk.preview.stimulus') },
    { id: 'questions', label: t('wizard.desk.preview.questions'), disabled: !hasQuestions },
    { id: 'thanks', label: t('wizard.desk.preview.thanks') },
  ]
  const ordered = order.map((id) => tabs.find((tab) => tab.id === id)!)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.75 }}>
      {/* The phone: a dark bezel with the screen inset. No island — the live
          screens have no safe area to keep clear of it. */}
      <Box
        sx={{
          position: 'relative',
          width,
          height,
          p: `${BEZEL}px`,
          borderRadius: `${Math.round(54 * scale + BEZEL)}px`,
          bgcolor: '#111218',
          boxShadow: '0 0 0 1px rgba(0,0,0,.08), 0 30px 60px -20px rgba(27,28,56,.35), inset 0 0 0 1.5px #3A3B46',
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: PHONE_W * scale, height: PHONE_H * scale, borderRadius: `${Math.round(46 * scale)}px`, overflow: 'hidden', position: 'relative', bgcolor: '#fff', isolation: 'isolate' }}>
          <Box sx={{ width: PHONE_W, height: PHONE_H, transform: `scale(${scale})`, transformOrigin: '0 0' }}>
            <Viewport width={PHONE_W} height={PHONE_H}>
              <Box key={`${screen}-${run}`} sx={{ display: 'contents' }}>
                {content}
              </Box>
            </Viewport>
          </Box>
        </Box>
      </Box>

      {/* Which screen: the journey in its own order, then restart. */}
      <Box role="tablist" aria-label={t('wizard.preview.title')} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 0.25, maxWidth: 380 }}>
        {ordered.map((tab) => {
          const on = tab.id === screen
          return (
            <Box
              key={tab.id}
              component="button"
              type="button"
              role="tab"
              aria-selected={on}
              disabled={tab.disabled}
              onClick={() => go(tab.id)}
              sx={{
                font: 'inherit',
                fontSize: 12.5,
                fontWeight: on ? 700 : 400,
                height: 28,
                px: 1.125,
                whiteSpace: 'nowrap',
                borderRadius: 99,
                border: 0,
                cursor: 'pointer',
                bgcolor: on ? c.ink : 'transparent',
                color: on ? '#fff' : c.ink72,
                transition: 'background-color .15s ease, color .15s ease',
                '&:hover:not(:disabled)': { bgcolor: on ? c.ink : 'rgba(27,28,56,.06)' },
                '&:disabled': { color: c.ink32, cursor: 'default' },
              }}
            >
              {tab.label}
            </Box>
          )
        })}
        <Box
          component="button"
          type="button"
          onClick={() => go(screen)}
          title={t('wizard.desk.preview.restart')}
          aria-label={t('wizard.desk.preview.restart')}
          sx={{ width: 28, height: 28, borderRadius: 99, border: 0, bgcolor: 'transparent', color: c.ink56, cursor: 'pointer', display: 'grid', placeItems: 'center', '&:hover': { bgcolor: 'rgba(27,28,56,.06)', color: c.ink } }}
        >
          <ReplayRoundedIcon sx={{ fontSize: 17 }} />
        </Box>
      </Box>

      {study.requires_desktop && (
        <Typography sx={{ fontSize: 12.5, color: c.ink56, textAlign: 'center', maxWidth: width, lineHeight: 1.45 }}>
          {t('wizard.desk.preview.desktopOnly')}
        </Typography>
      )}
    </Box>
  )
}

/** The live question screen keeps its answers in the journey; here they live in the preview. */
function PreviewQuestions({ study, stepIdx, onFinish }: { study: RespondentStudy; stepIdx: number; onFinish: () => void }) {
  const [answers, setAnswers] = useState<Record<number, { choices: number[]; text_answer: string }>>({})
  return <Questions questions={study.questions} answers={answers} setAnswers={setAnswers} onFinish={async () => onFinish()} stepIdx={stepIdx} />
}
